import { z } from "zod";
import { fetchApi } from "@/lib/api-helpers";

const API_VER = "v202506";

// PayPal order creation response
export const paypalOrderResponseSchema = z.object({
  success: z.boolean(),
  order_id: z.string(),
  authorization_id: z.string().optional(),
  payment_uuid: z.string().optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// PayPal authorization response
export const paypalAuthorizationResponseSchema = z.object({
  success: z.boolean(),
  order_id: z.string(),
  authorization_id: z.string(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// PayPal vault setup token response (create_order, vault path — no `success` key)
export const paypalVaultOrderResponseSchema = z.object({
  setup_token_id: z.string(),
  approve_url: z.string().optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// PayPal vault authorization response (authorize_order, vault path)
export const paypalVaultAuthorizationResponseSchema = z.object({
  success: z.boolean(),
  payment_method_id: z.union([z.number(), z.string()]).optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// PayPal capture response
export const paypalCaptureResponseSchema = z.object({
  success: z.boolean(),
  capture_id: z.string().optional(),
  order_id: z.string().optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// PayPal payment method creation response
export const paypalPaymentMethodResponseSchema = z.object({
  success: z.boolean().optional(),
  payment_method: z
    .object({
      id: z.number(),
      type: z.string(),
      token: z.string(),
    })
    .optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// PayPal SDK Token response schema
export const paypalSdkTokenResponseSchema = z.object({
  client_sdk: z.object({
    token: z.string(),
  }),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

// FastLane lookup response schema
export const fastlaneLookupResponseSchema = z.object({
  eligible: z.boolean(),
  customer_context_id: z.string().optional(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

export const shippingChangeResponseSchema = z.object({
  success: z.boolean(),
  order_id: z.string().nullable(),
  meta: z
    .object({
      request_id: z.string().optional(),
      timestamp: z.string().optional(),
    })
    .optional(),
});

export type PayPalOrderResponse = z.infer<typeof paypalOrderResponseSchema>;
export type PayPalAuthorizationResponse = z.infer<
  typeof paypalAuthorizationResponseSchema
>;
export type PayPalVaultOrderResponse = z.infer<
  typeof paypalVaultOrderResponseSchema
>;
export type PayPalVaultAuthorizationResponse = z.infer<
  typeof paypalVaultAuthorizationResponseSchema
>;
export type PayPalCaptureResponse = z.infer<typeof paypalCaptureResponseSchema>;
export type PayPalPaymentMethodResponse = z.infer<
  typeof paypalPaymentMethodResponseSchema
>;
export type PayPalSdkTokenResponse = z.infer<
  typeof paypalSdkTokenResponseSchema
>;
export type FastlaneLookupResponse = z.infer<
  typeof fastlaneLookupResponseSchema
>;
export type ShippingChangeResponse = z.infer<
  typeof shippingChangeResponseSchema
>;

// path param is payment account UUID
export async function getPaypalSdkToken({
  paymentAccountId,
}: {
  paymentAccountId: string;
}) {
  return fetchApi({
    endpoint: `/paypal/client_sdk_token/${paymentAccountId}`,
    method: "GET",
    outputSchema: paypalSdkTokenResponseSchema,
  });
}

// Create PayPal order
export async function createPaypalOrder({
  cartToken,
  amount,
  description = "Order",
  paymentTokenId,
  fundingSource,
  isPayLater = false,
  appSwitch = false,
}: {
  cartToken: string;
  amount: string;
  description?: string;
  paymentTokenId?: string;
  fundingSource?: string;
  isPayLater?: boolean;
  appSwitch?: boolean;
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/paypal/create_order`,
    method: "POST",
    input: {
      amount,
      description,
      payment_token_id: paymentTokenId,
      funding_source: fundingSource,
      is_pay_later: isPayLater,
      is_app_switch: appSwitch,
    },
    outputSchema: paypalOrderResponseSchema,
  });
}

// Create PayPal vault setup token. Posts the same create_order endpoint and
// body shape as createPaypalOrder; the backend decides vault-vs-charge from the
// cart and returns a setup_token_id on the vault path.
export async function createPaypalVaultSetupToken({
  cartToken,
  amount,
  description = "Order",
  paymentTokenId,
  fundingSource = "paypal",
  isPayLater = false,
  appSwitch = false,
}: {
  cartToken: string;
  amount: string;
  description?: string;
  paymentTokenId?: string;
  fundingSource?: string;
  isPayLater?: boolean;
  appSwitch?: boolean;
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/paypal/create_order`,
    method: "POST",
    input: {
      amount,
      description,
      payment_token_id: paymentTokenId,
      funding_source: fundingSource,
      is_pay_later: isPayLater,
      is_app_switch: appSwitch,
    },
    outputSchema: paypalVaultOrderResponseSchema,
  });
}

// Authorize PayPal order
export async function authorizePaypalOrder({
  cartToken,
  orderId,
}: {
  cartToken: string;
  orderId: string;
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/paypal/authorize_order`,
    method: "POST",
    input: {
      order_id: orderId,
    },
    outputSchema: paypalAuthorizationResponseSchema,
  });
}

// Authorize a PayPal vault setup token. Posts the authorize_order endpoint with
// the setup_token_id and returns the persisted payment_method_id on success.
export async function authorizePaypalVaultOrder({
  cartToken,
  setupTokenId,
}: {
  cartToken: string;
  setupTokenId: string;
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/paypal/authorize_order`,
    method: "POST",
    input: {
      setup_token_id: setupTokenId,
    },
    outputSchema: paypalVaultAuthorizationResponseSchema,
  });
}

// Capture PayPal payment
export async function capturePaypalPayment({
  cartToken,
  orderId,
  amount,
}: {
  cartToken: string;
  orderId: string;
  amount: string;
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/paypal/capture_payment`,
    method: "POST",
    input: {
      order_id: orderId,
      amount,
    },
    outputSchema: paypalCaptureResponseSchema,
  });
}

// prefer top-level payment_account (UUID) over payment_method.payment_account_id
export async function createPaypalPaymentMethod({
  cartToken,
  orderId,
  authorizationId,
  payment_account,
  payment_account_id,
}: {
  cartToken: string;
  orderId: string;
  authorizationId: string;
  payment_account?: string;
  payment_account_id?: number;
}) {
  const payload: {
    payment_account?: string;
    payment_method: {
      order_id: string;
      token: string;
      payment_account_id?: number;
    };
  } = {
    payment_method: {
      order_id: orderId,
      token: authorizationId,
    },
  };

  if (payment_account) {
    payload.payment_account = payment_account;
  } else if (payment_account_id) {
    payload.payment_method.payment_account_id = payment_account_id;
  }

  return fetchApi({
    endpoint: `/carts/${cartToken}/payment_methods/paypal`,
    method: "POST",
    input: payload,
    outputSchema: paypalPaymentMethodResponseSchema,
  });
}

// Handle shipping address change
export async function handleShippingAddressChange({
  cartToken,
  orderId,
  shippingAddress,
}: {
  cartToken: string;
  orderId: string;
  shippingAddress: {
    country_code: string;
    state?: string;
    city?: string;
    postal_code?: string;
    address_line_1?: string;
    address_line_2?: string;
  };
}) {
  return fetchApi({
    endpoint: `/${API_VER}/carts/${cartToken}/paypal/shipping_address_change`,
    method: "POST",
    input: {
      order_id: orderId,
      shipping_address: shippingAddress,
    },
    outputSchema: shippingChangeResponseSchema,
  });
}

export async function handleShippingOptionsChange({
  cartToken,
  orderId,
  shippingMethodId,
}: {
  cartToken: string;
  orderId: string;
  shippingMethodId: string;
}) {
  return fetchApi({
    endpoint: `/v202506/carts/${cartToken}/paypal/shipping_method_change`,
    method: "POST",
    input: {
      order_id: orderId,
      shipping_method_id: shippingMethodId,
    },
    inputSchema: z.object({
      order_id: z.string(),
      shipping_method_id: z.string(),
    }),
    outputSchema: shippingChangeResponseSchema,
  });
}
